// frontend/src/components/WorkshopDashboard/ScheduleRequestModal.js
// Asks for the appointment slot behind the "Planifier" action. Booking is
// immediate — the client is notified, not asked — so the copy says so plainly
// rather than implying the date is a proposal.

import React, { useState, useEffect } from 'react';
import { Modal, Field, Button, ErrorNote, Note, inputClass, textareaClass } from '../ui/Iparc';

// <input type="datetime-local"> only accepts YYYY-MM-DDTHH:mm. A full ISO
// string with seconds and a zone is silently rejected and the field stays
// empty, so the value has to be shifted into local time and trimmed.
const toLocalInput = (d) =>
  new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

const defaultSlot = (request) => {
  const existing = request?.rescheduledDate || request?.date;
  const d = existing ? new Date(existing) : null;
  if (d && !Number.isNaN(d.getTime()) && d.getTime() > Date.now()) return toLocalInput(d);
  // Nothing usable on the request — open on tomorrow morning rather than a
  // blank field the workshop has to fill from scratch.
  const t = new Date();
  t.setDate(t.getDate() + 1);
  t.setHours(9, 0, 0, 0);
  return toLocalInput(t);
};

export default function ScheduleRequestModal({ request, onClose, onSubmit, submitting, error }) {
  const [date, setDate] = useState('');
  const [note, setNote] = useState('');

  // Reset per request, so reopening on a different job never inherits the
  // previous one's slot or note.
  useEffect(() => {
    if (!request) return;
    setDate(defaultSlot(request));
    setNote('');
  }, [request]);

  const plate = request?.vehicleId?.plateNumber || 'Demande';
  const isPast = date && new Date(date).getTime() < Date.now();

  return (
    <Modal
      open={!!request}
      onClose={onClose}
      subtitle={plate}
      title="Planifier les travaux"
      footer={
        <>
          <Button onClick={onClose}>Annuler</Button>
          <Button
            variant="primary"
            disabled={!date || submitting}
            onClick={() => onSubmit(new Date(date).toISOString(), note)}
          >
            {submitting ? 'Enregistrement…' : 'Confirmer le rendez-vous'}
          </Button>
        </>
      }
    >
      {error && <ErrorNote>{error}</ErrorNote>}

      <Field label="Date et heure du rendez-vous">
        <input
          type="datetime-local"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className={inputClass}
        />
      </Field>

      {isPast && <Note tone="warn">Cette date est déjà passée.</Note>}

      <Field
        label="Message au client"
        hint="Optionnel — ajouté à la notification et conservé sur la demande."
      >
        <textarea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className={textareaClass}
          placeholder="Ex : merci d'apporter la carte grise."
        />
      </Field>

      <Note tone="ok">
        Le rendez-vous est confirmé immédiatement. Le client reçoit une notification et un SMS.
      </Note>
    </Modal>
  );
}
